<template>
    <div class="flex items-center filter-tags__wrapper">
      <div
        v-for="(filter, index) in allFilters"
        :key="index"
        class="filter__tag ml-0 mr-3"
        :class="{'el-tag--danger': isNegativeOperator(filter.operator)}"
        @mouseenter="onTagMouseEnter(index)"
        @mouseleave="onTagMouseLeave">
        <div class="filter__tag--content">
          <div v-if="currentHoveredTag === index" :key="`${index}-hover`" class="filter__tag--edit">
            <fg-input class="filter-hover__element filter-hover__element--checkbox">
              <base-checkbox v-model="filter.selected"
                             :type="isNegativeOperator(filter.operator) ? 'info': ''"
                             class="mt-0"/>
            </fg-input>
            <i v-if="isPositiveOperator(filter.operator)" @click="toggleOperator(filter, index)" class="fas fa-search-minus"></i>
            <i v-if="isNegativeOperator(filter.operator)" @click="toggleOperator(filter, index)" class="fas fa-search-plus"></i>
            <i class="nc-icon nc-simple-remove" @click="onFilterDelete(index)"></i>
            <el-popover v-model="showEditFilter"
                        trigger="manual"
                        popper-class="popover-white">
              <filter-popover-content :title="$t('filters.tags.editFilter')"
                                      :filter="filter"
                                      :fields="fields"
                                      @cancel="showEditFilter = false"
                                      @save-filter="val => onEditFilterSave(val, index)"
              />
              <i slot="reference" class="nc-icon nc-ruler-pencil edit__icon" @click="onFilterEdit(filter)"></i>
            </el-popover>
          </div>
          <div v-else :key="`${index}-no-hover`" class="filter__tag--edit">
            <span>{{filter.label || filter.field.name}}</span>&nbsp;
            <span class="font-weight-bold">&nbsp;
              {{filter.operator}}
              &nbsp;
            </span>
            <b>{{filterValuesText(filter)}}</b>
          </div>
        </div>
      </div>
      <el-popover placement="bottom"
                  v-model="showAddFilter"
                  trigger="manual"
                  popper-class="popover-white">
        <filter-popover-content :title="$t('filters.tags.addFilter')"
                                :fields="fields"
                                @cancel="showAddFilter = false"
                                @save-filter="onNewFilterSave"/>
        <base-button slot="reference" @click="displayAddFilter"
                     class="my-0"
                     round
                     link
                     type="info">
          {{$t('filters.tags.addNew')}}
        </base-button>
      </el-popover>

      <el-popover placement="bottom"
                  v-if="allFilters.length"
                  v-model="showSavePreset"
                  trigger="manual"
                  popper-class="popover-white">
        <filter-save-preset :title="$t('filters.tags.savePreset')"
                            @cancel="showSavePreset = false"
                            @save-filter="onSavePreset">
        </filter-save-preset>
        <base-button slot="reference" @click="displaySavePreset"
                     class="my-0"
                     round
                     link
                     type="primary">
          {{$t('filters.tags.savePreset')}}
        </base-button>
      </el-popover>
    </div>
</template>

<script>
import { Popover } from 'element-ui'
import clone from 'lodash/clone'
import FilterPopoverContent from './FilterTags/FilterPopoverContent'
import FilterSavePreset from './FilterTags/FilterSavePreset'
import { FRIENDLY_DATE_FORMAT } from 'src/config/dateUtils'

export default {
  name: 'filter-tags',
  props: {
    fields: {
      type: Object,
      default: () => ({})
    },
    entity: {
      type: String
    },
  },
  components: {
    ElPopover: Popover,
    FilterPopoverContent,
    FilterSavePreset
  },
  data () {
    return {
      filters: [],
      allFilters: [],
      currentHoveredTag: -1,
      showEditFilter: false,
      showAddFilter: false,
      showSavePreset: false,
      selectedFilter: null
    }
  },
  computed: {
    vuexFilters() {
      let filtersState = this.$store.state.filters
      if (filtersState.activeFilters.length || this.$route.query.filter) {
        return filtersState.activeFilters
      }
      return filtersState.tableFilters[this.entity] || []
    }
  },
  methods: {
    async saveActiveFilters (filters) {
      this.allFilters = filters
      this.$emit('change', filters)
      this.$store.commit('filters/SET_TABLE_FILTERS', {
        entity: this.entity,
        value: filters
      })
    },
    onTagMouseEnter (index) {
      if (!this.showEditFilter) {
        this.currentHoveredTag = index
      }
    },
    onTagMouseLeave () {
      if (!this.showEditFilter) {
        this.currentHoveredTag = -1
      }
    },
    onFilterEdit (filter) {
      this.selectedFilter = filter
      this.displayEditFilter()
    },
    onFilterDelete (index) {
      let filters = clone(this.allFilters)
      filters.splice(index, 1)
      this.saveActiveFilters(filters)
    },
    onNewFilterSave (newFilter) {
      let filters = clone(this.allFilters)
      filters.push(newFilter)
      this.saveActiveFilters(filters)
      this.showAddFilter = false
      this.onTagMouseLeave()
    },
    onSavePreset(preset) {
      this.$store.commit('filterPresets/ADD_FILTER_PRESET', {
        name: preset.name,
        entity: this.entity,
        filters: this.allFilters
      })
      this.showSavePreset = false
    },
    onEditFilterSave (filter, index) {
      let filters = clone(this.allFilters)
      filters.splice(index, 1, filter)
      this.saveActiveFilters(filters)
      this.showEditFilter = false
      this.onTagMouseLeave()
    },
    displayAddFilter () {
      this.showEditFilter = false
      this.showAddFilter = true
    },
    displayEditFilter () {
      this.showAddFilter = false
      this.showEditFilter = true
    },
    displaySavePreset() {
      this.showSavePreset = true;
      this.showAddFilter = false
      this.showEditFilter = false
    },
    isNegativeOperator (operator) {
      return operator === 'ISNOT'
    },
    isPositiveOperator (operator) {
      return operator === 'IS'
    },
    toggleOperator (filter, index = -1) {
      if (filter.operator === 'ISNOT') {
        filter.operator = 'IS'
      } else if (filter.operator === 'IS') {
        filter.operator = 'ISNOT'
      }
      let filters = clone(this.allFilters)
      filters.splice(index, 1, filter)
      this.saveActiveFilters(filters)
    },
    filterValuesText (filter) {
      let values = filter.valuesLabel || filter.values
      let filterType = filter.field.type
      if (filter.operator === '><') {
        let { min, max } = filter
        if (filterType === 'date') {
          min = this.$formatDate(min, FRIENDLY_DATE_FORMAT)
          max = this.$formatDate(max, FRIENDLY_DATE_FORMAT)
        }
        return `${this.$t('generic.min')}: ${min || '-'} ${this.$t('generic.max')}: ${max || '-'} `
      }
      if (filterType === 'date') {
        return this.$formatDate(values, FRIENDLY_DATE_FORMAT)
      }

      if (Array.isArray(values)) {
        return values.join(', ')
      }
      return values
    }
  },
  watch: {
    vuexFilters: {
      immediate: true,
      handler: function(newVal) {
        this.saveActiveFilters(newVal)
      }
    }
  }
}
</script>
<style lang="scss">

  .filter-tags__wrapper {
    padding-bottom: 10px;
    .filter__tag {
      min-width: 160px;
      min-height: 30px;
      @apply inline-flex justify-center cursor-pointer mb-0;
      @apply bg-gray-200 text-gray-800 px-3 rounded-full;
      &:hover {
        margin-bottom: 0;
      }
    }

    .filter__tag--content {
      @apply text-gray-800 flex justify-center items-center;
    }
    .filter__tag--edit {
      @apply h-full flex items-center;

      .edit__icon {
        margin-top: 6px;
      }
      i, .filter-hover__element {
        font-size: 14px;
        padding-left: 8px;
        padding-right: 8px;
        border-left: 1px solid rgba(255, 255, 255, 0.7);
        &:hover {
          font-weight: bold;
        }
      }
      .filter-hover__element {
        padding-right: 0;
        position: relative;
        top: -3px;
      }
      .filter-hover__element--checkbox {
        top: 0;
        height: 16px;

        .form-group {
          width: 28px;
        }
        .form-check-sign:before,
        .form-check-sign:after {
          top: -18px;
          width: 20px;
          height: 20px;
        }
        .form-check-sign:after {
          top: -21px;
          font-size: 12px;
        }
      }
    }
  }
</style>
